import { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

interface AnimatedCounterProps {
  /** Display string like "100+", "99.9%", "4", "24/7". */
  value: string;
  className?: string;
  durationMs?: number;
}

// Split a value into [prefix, number, suffix]. Returns null for values that
// don't contain a single clean number (e.g. "24/7"), which render statically.
const parse = (value: string): [string, string, string] | null => {
  const match = value.match(/^(\D*?)(\d+(?:\.\d+)?)(\D*)$/);
  if (!match) return null;
  return [match[1], match[2], match[3]];
};

const AnimatedCounter = ({ value, className, durationMs = 1600 }: AnimatedCounterProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -40px 0px' });
  const reduce = useReducedMotion();
  const parsed = parse(value);

  const [display, setDisplay] = useState(() =>
    parsed ? `${parsed[0]}0${parsed[2]}` : value
  );

  useEffect(() => {
    if (!parsed) {
      setDisplay(value);
      return;
    }
    if (reduce) {
      setDisplay(value);
      return;
    }
    if (!inView) return;

    const [prefix, numStr, suffix] = parsed;
    const target = parseFloat(numStr);
    const decimals = numStr.includes('.') ? numStr.split('.')[1].length : 0;
    let raf = 0;
    const startTime = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplay(`${prefix}${(target * eased).toFixed(decimals)}${suffix}`);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, reduce, value]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

export default AnimatedCounter;
